import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import uniqBy from 'lodash.uniqby';

import { type IError } from '@wf/sw-ui-kit';

import { arrayBracketsIndexRegexp, arrayDotIndexRegexp } from '../../../constants/regex';
import { type ILicenseTypeConfigResponse, type ILpcoResponse } from '../types/response';
import { type IConfig, type TValidationConfig } from '../types/configs';
import { type ArrayFields } from '../types/fieldEnums';

import { resetFieldConfig, setConfigs } from '../fieldConfig';

interface ILicenseType {
  idMap: Record<string, ILicenseTypeConfigResponse>;
  loading: boolean;
  selectedLicenseTypeId: Nullable<number>;
  parentLicenseTypeId: Nullable<number>;
}

interface IConfiguration {
  configs: Nullable<IConfig>;
  unionOfEditableFields: Set<string>;
}

export interface ILicenseState {
  lpco: {
    data: Nullable<ILpcoResponse>;
    loading: boolean;
    isSubmitting: boolean;
    isFetched: boolean;
  };
  parentLpco: {
    data: Nullable<ILpcoResponse>;
    loading: boolean;
  };
  licenseType: ILicenseType;
  userReferenceNumber: Nullable<string>;
  configuration: IConfiguration;
  validationConfig: TValidationConfig;
  errors: IError[];
  queryMessage: string;
  mandatoryAttachmentCodes: string[];
}

const initialState: ILicenseState = {
  lpco: {
    data: null,
    loading: false,
    isSubmitting: false,
    isFetched: false,
  },
  parentLpco: {
    data: null,
    loading: false,
  },
  licenseType: {
    idMap: {},
    loading: false,
    selectedLicenseTypeId: null,
    parentLicenseTypeId: null,
  },
  userReferenceNumber: null,
  configuration: {
    configs: null,
    unionOfEditableFields: new Set(),
  },
  validationConfig: {},
  errors: [],
  queryMessage: '',
  mandatoryAttachmentCodes: [],
};

const licenseSlice = createSlice({
  name: 'license',
  initialState,
  reducers: {
    setLpco: (state, { payload }: PayloadAction<Nullable<ILpcoResponse>>) => {
      state.lpco.data = payload;
    },
    setLpcoLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.lpco.loading = payload;
    },
    setParentLpco: (state, { payload }: PayloadAction<Nullable<ILpcoResponse>>) => {
      state.parentLpco.data = payload;
    },
    setParentLpcoLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.parentLpco.loading = payload;
    },
    setLpcoSubmitting: (state, { payload }: PayloadAction<boolean>) => {
      state.lpco.isSubmitting = payload;
    },
    setLpcoIsFetched: (state, { payload }: PayloadAction<boolean>) => {
      state.lpco.isFetched = payload;
    },
    setLicenseType: (state, { payload }: PayloadAction<ILicenseTypeConfigResponse>) => {
      const { id } = payload.licenseTypeResponse;
      state.licenseType.idMap[id] = payload;
      state.licenseType.selectedLicenseTypeId = id;
    },
    setParentLicenseType: (state, { payload }: PayloadAction<ILicenseTypeConfigResponse>) => {
      const { id } = payload.licenseTypeResponse;
      state.licenseType.idMap[id] = payload;
      state.licenseType.parentLicenseTypeId = id;
    },
    setSelectedLicenseTypeId: (state, { payload }: PayloadAction<Nullable<number>>) => {
      state.licenseType.selectedLicenseTypeId = payload;
    },
    setParentLicenseTypeId: (state, { payload }: PayloadAction<Nullable<number>>) => {
      state.licenseType.parentLicenseTypeId = payload;
    },
    setLicenseTypeLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.licenseType.loading = payload;
    },
    setConfiguration: (state, { payload }: PayloadAction<IConfig>) => {
      const configuration = setConfigs(payload);
      state.configuration = configuration;
    },
    resetConfiguration: (state) => {
      resetFieldConfig();
      state.configuration = {
        configs: null,
        unionOfEditableFields: new Set(),
      };
    },
    setUserReferenceNumber: (state, { payload }: PayloadAction<Nullable<string>>) => {
      state.userReferenceNumber = payload;
    },
    setErrors: (state, { payload }: PayloadAction<IError[]>) => {
      state.errors = payload;
    },
    mergeErrors: (state, { payload }: PayloadAction<IError[]>) => {
      state.errors = uniqBy([...payload, ...state.errors], 'fieldName');
    },
    resetErrors: (state) => {
      if (state.errors.length) {
        state.errors = [];
      }
    },
    removeArrayFieldError: (
      state,
      {
        payload: { entity, index },
      }: PayloadAction<{
        entity: ArrayFields;
        index: number;
      }>
    ) => {
      state.errors = state.errors
        .filter((error) => error.entity !== entity || error.index !== index)
        .map((error) => {
          if (error.entity === entity && (error.index as number) > index) {
            const newIndex = (error.index as number) - 1;

            return {
              ...error,
              field: error.field.replace(arrayBracketsIndexRegexp, `[${newIndex}]`),
              fieldName: error.fieldName.replace(arrayDotIndexRegexp, `.${newIndex}.`),
              index: newIndex,
            };
          }

          return error;
        });
    },
    setValidationConfig: (state, { payload }: PayloadAction<TValidationConfig>) => {
      state.validationConfig = payload;
    },
    addQueryMessage: (state, { payload }: PayloadAction<string>) => {
      state.queryMessage = state.queryMessage ? `${state.queryMessage}\n\n${payload}` : payload;
    },
    replaceQueryMessage: (state, { payload }: PayloadAction<string>) => {
      state.queryMessage = payload;
    },
    removeQueryMessage: (state) => {
      state.queryMessage = '';
    },
    setMandatoryAttachmentCodes: (state, { payload }: PayloadAction<string[]>) => {
      state.mandatoryAttachmentCodes = payload;
    },
  },
});

export const {
  setLpco,
  setLpcoLoading,
  setLpcoSubmitting,
  setParentLpco,
  setParentLpcoLoading,
  setLpcoIsFetched,
  setLicenseType,
  setParentLicenseType,
  setSelectedLicenseTypeId,
  setParentLicenseTypeId,
  setLicenseTypeLoading,
  setConfiguration,
  resetConfiguration,
  setUserReferenceNumber,
  setErrors,
  resetErrors,
  removeArrayFieldError,
  setValidationConfig,
  addQueryMessage,
  replaceQueryMessage,
  removeQueryMessage,
  mergeErrors,
  setMandatoryAttachmentCodes,
} = licenseSlice.actions;

export default licenseSlice;
